import React, { useRef } from 'react';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { CHART_COLORS } from 'shared/constants/colors';
import theme from 'shared/constants/theme.json';
import Loading from 'components/Loading';

echarts.use([
  CanvasRenderer,
  BarChart,
  LineChart,
  GridComponent,
  TooltipComponent,
]);

const DEFAULT_CHART_OPTIONS = {
  color: CHART_COLORS,
  textStyle: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: theme.fontFamily,
  },
  grid: { containLabel: true },
};

function getDataSet(results, dimensions, metrics, sorter) {
  return results?.map(r => ({
    source: r.data.map(d => ({
      id: dimensions.map(di => d[di.name]).join(', '),
      ...d,
    })).sort(sorter),
    dimensions: ['id', ...dimensions.map(d => d.name), ...metrics.map(m => m.name)],
  }));
}

const options = {
  bar(results, dimensions, metrics, segments) {
    return results ? {
      ...DEFAULT_CHART_OPTIONS,
      dataset: getDataSet(results, dimensions, metrics, (a, b) => a[metrics[0].name] - b[metrics[0].name]),
      tooltip: {
        trigger: 'axis',
      },
      yAxis: {
        type: 'category',
      },
      xAxis: {
        type: 'value',
      },
      series: segments.map(s => ({
        type: 'bar',
        encode: {
          x: metrics[0].name,
          y: 'id',
        },
        name: s?.title || s.id,
        color: s.color,
        datasetIndex: results.findIndex(r => r.segmentId === s.id),
      })),
    } : {};
  },
  line(results, dimensions, metrics, segments) {
    return results ? {
      ...DEFAULT_CHART_OPTIONS,
      dataset: getDataSet(results, dimensions, metrics, (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
      tooltip: {
        trigger: 'axis',
      },
      yAxis: {
        type: 'value',
      },
      xAxis: {
        type: 'time',
      },
      series: segments.map(s => ({
        type: 'line',
        encode: {
          x: 'date',
          y: metrics[0].name,
        },
        name: s?.title || s.id,
        color: s.color,
        datasetIndex: results.findIndex(r => r.segmentId === s.id),
      })),
    } : {};
  },
};

const ChartResult = ({ results, resultConfig, loading, globalFilter, presentationType }) => {
  const height = 500;

  const echartsInstance = useRef();

  // const saveAsImage = () => {
  //   const base64Image = echartsInstance.current.getEchartsInstance().getDataURL({
  //     pixelRatio: 2,
  //     backgroundColor: WHITE,
  //   });

  //   const link = document.createElement('a');

  //   link.setAttribute('href', base64Image);
  //   link.setAttribute('download', title?.replace(/[^a-z0-9åäöÅÄÖ-]/gi, '_').replace(/__/, '_').replace(/^_|_$|/g, '') || 'extellio-chart');
  //   link.click();
  // };

  if (loading) {
    return <Loading fullsize={false} style={{ padding: '2rem' }} />;
  }

  const metrics = resultConfig?.metrics || [];
  const dimensions = resultConfig?.dimensions || [];
  const segments = [globalFilter.mainSegment, ...globalFilter.comparisonSegments];

  const option = options[presentationType]?.(results, dimensions, metrics, segments) || {};

  return (
    <ReactEchartsCore
      echarts={echarts}
      style={{ height }}
      option={option}
      // onEvents={{
      //   legendselectchanged: onLegendChange,
      //   legendselectall: onLegendChange,
      //   legendinverseselect: onLegendChange,
      //   click: onChartClick,
      // }}
      notMerge
      ref={echartsInstance}
    />
  );
};

export default ChartResult;
